import React, { useEffect, useState, useCallback } from 'react'
import { FlatList, RefreshControl } from 'react-native'
import {
  ArrowLeft, Plus, FileText, Send, CheckCircle, Truck, Package, XCircle, ClipboardList,
  type LucideIcon,
} from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Spinner,
  Text,
  TopBar,
  XStack,
  YStack,
} from '@fliisi/pollen'

const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70',
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'PurchaseOrders'>
  route: RouteProp<RootStackParamList, 'PurchaseOrders'>
}

interface PurchaseOrder {
  id: string
  order_number: string
  order_date: string
  expected_delivery_date: string | null
  status: string
  subtotal: number
  total_amount: number
  supplier: { id: string; name: string }
  items_count?: number
}

type FilterType = 'all' | 'draft' | 'sent' | 'confirmed' | 'shipped' | 'received'

const STATUS_CONFIG: Record<string, { label: string; labelFi: string; color: string; icon: LucideIcon }> = {
  draft: { label: 'Draft', labelFi: 'Luonnos', color: '#9E9E9E', icon: FileText },
  sent: { label: 'Sent', labelFi: 'Lähetetty', color: '#2196F3', icon: Send },
  confirmed: { label: 'Confirmed', labelFi: 'Vahvistettu', color: '#4CAF50', icon: CheckCircle },
  shipped: { label: 'Shipped', labelFi: 'Lähetetty', color: '#FF9800', icon: Truck },
  received: { label: 'Received', labelFi: 'Vastaanotettu', color: '#8BC34A', icon: Package },
  cancelled: { label: 'Cancelled', labelFi: 'Peruutettu', color: '#F44336', icon: XCircle },
}

export default function PurchaseOrdersScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t, language } = useLanguage()
  const [orders, setOrders] = useState<PurchaseOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [filter, setFilter] = useState<FilterType>('all')

  useEffect(() => {
    fetchOrders()
  }, [storeId])

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('purchase_orders')
        .select(`
          id, order_number, order_date, expected_delivery_date, status, subtotal, total_amount,
          supplier:suppliers(id, name)
        `)
        .eq('flea_market_id', storeId)
        .order('order_date', { ascending: false })

      if (error) throw error

      const orderIds = (data || []).map(o => o.id)
      let itemCounts: Record<string, number> = {}

      if (orderIds.length > 0) {
        const { data: items } = await supabase
          .from('purchase_order_items')
          .select('purchase_order_id')
          .in('purchase_order_id', orderIds)

        items?.forEach(item => {
          itemCounts[item.purchase_order_id] = (itemCounts[item.purchase_order_id] || 0) + 1
        })
      }

      setOrders((data || []).map(o => ({
        ...o,
        supplier: (o.supplier as any) as { id: string; name: string },
        items_count: itemCounts[o.id] || 0,
      })))
    } catch (error) {
      console.error('Error fetching orders:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const onRefresh = useCallback(() => {
    setRefreshing(true)
    fetchOrders()
  }, [storeId])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  }

  const formatCurrency = (amount: number) => `€${amount.toFixed(2)}`

  const getStatusLabel = (status: string) => {
    const config = STATUS_CONFIG[status]
    if (!config) return status
    return language === 'fi' ? config.labelFi : config.label
  }

  const filteredOrders = orders.filter(o => filter === 'all' || o.status === filter)

  const statusCounts = {
    all: orders.length,
    draft: orders.filter(o => o.status === 'draft').length,
    sent: orders.filter(o => o.status === 'sent').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    received: orders.filter(o => o.status === 'received').length,
  }

  const renderOrderItem = ({ item }: { item: PurchaseOrder }) => {
    const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft
    const StatusIcon = statusConfig.icon

    return (
      <YStack
        backgroundColor="$backgroundCard"
        borderRadius="$lg"
        padding="$md"
        marginBottom="$sm"
        elevation={2}
        onPress={() => navigation.navigate('PurchaseOrderDetail', { storeId, orderId: item.id })}
        pressStyle={{ opacity: 0.8, scale: 0.98 }}
        animation="quick"
        cursor="pointer"
      >
        <XStack justifyContent="space-between" alignItems="flex-start" marginBottom={14}>
          <YStack>
            <Text fontSize={16} fontWeight="bold" color="$color">{item.order_number}</Text>
            <Text fontSize={14} color="$colorSecondary" marginTop={2}>{item.supplier?.name || 'Unknown Supplier'}</Text>
          </YStack>
          <XStack alignItems="center" gap="$xs" paddingHorizontal={10} paddingVertical={6} borderRadius="$full" backgroundColor={statusConfig.color + '20'}>
            <StatusIcon size={12} color={statusConfig.color} />
            <Text fontSize={12} fontWeight="600" color={statusConfig.color}>{getStatusLabel(item.status)}</Text>
          </XStack>
        </XStack>

        <XStack justifyContent="space-between" paddingTop="$sm" borderTopWidth={1} borderTopColor="$borderColor">
          <YStack alignItems="center">
            <Text fontSize={10} color="$colorSecondary" marginBottom={2}>{t('order_date') || 'Order Date'}</Text>
            <Text fontSize={14} fontWeight="600" color="$color">{formatDate(item.order_date)}</Text>
          </YStack>
          {item.expected_delivery_date && (
            <YStack alignItems="center">
              <Text fontSize={10} color="$colorSecondary" marginBottom={2}>{t('expected') || 'Expected'}</Text>
              <Text fontSize={14} fontWeight="600" color="$color">{formatDate(item.expected_delivery_date)}</Text>
            </YStack>
          )}
          <YStack alignItems="center">
            <Text fontSize={10} color="$colorSecondary" marginBottom={2}>{t('items') || 'Items'}</Text>
            <Text fontSize={14} fontWeight="600" color="$color">{item.items_count}</Text>
          </YStack>
          <YStack alignItems="center">
            <Text fontSize={10} color="$colorSecondary" marginBottom={2}>{t('total') || 'Total'}</Text>
            <Text fontSize={14} fontWeight="600" color={merchantColors.primary}>{formatCurrency(item.total_amount)}</Text>
          </YStack>
        </XStack>
      </YStack>
    )
  }

  const FilterButton = ({ value, label, count }: { value: FilterType; label: string; count: number }) => (
    <XStack
      alignItems="center"
      paddingHorizontal="$sm"
      paddingVertical={6}
      borderRadius="$full"
      backgroundColor={filter === value ? merchantColors.primary : '$backgroundSecondary'}
      onPress={() => setFilter(value)}
      pressStyle={{ opacity: 0.8, scale: 0.98 }}
      animation="quick"
      cursor="pointer"
    >
      <Text fontSize={13} fontWeight="500" color={filter === value ? '#fff' : '$colorSecondary'}>
        {label}
      </Text>
      {count > 0 && (
        <XStack marginLeft={6} backgroundColor={filter === value ? 'rgba(255,255,255,0.3)' : '$borderColor'} paddingHorizontal={6} paddingVertical={2} borderRadius={10}>
          <Text fontSize={11} fontWeight="600" color={filter === value ? '#fff' : '$colorSecondary'}>{count}</Text>
        </XStack>
      )}
    </XStack>
  )

  if (loading) {
    return <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
  }

  return (
    <YStack f={1} backgroundColor="$background">
      {/* Header */}
      <XStack justifyContent="space-between" alignItems="center" paddingHorizontal="$md" paddingTop="$md" paddingBottom="$md" backgroundColor="$backgroundCard" borderBottomWidth={1} borderBottomColor="$borderColor">
        <XStack alignItems="center" onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
          <ArrowLeft size={18} color={merchantColors.primary} />
          <Text color={merchantColors.primary} fontSize={16} fontWeight="500" marginLeft="$xs">{t('back') || 'Back'}</Text>
        </XStack>
        <Text fontSize={18} fontWeight="bold" color="$color">{t('purchase_orders') || 'Purchase Orders'}</Text>
        <YStack
          width={36} height={36} borderRadius="$full" backgroundColor={merchantColors.primary}
          jc="center" ai="center"
          onPress={() => navigation.navigate('PurchaseOrderDetail', { storeId })}
          pressStyle={{ opacity: 0.8, scale: 0.98 }}
          animation="quick"
          cursor="pointer"
        >
          <Plus size={20} color="#fff" />
        </YStack>
      </XStack>

      {/* Filters */}
      <XStack paddingHorizontal="$md" paddingVertical="$sm" backgroundColor="$backgroundCard" gap={6} flexWrap="wrap" borderBottomWidth={1} borderBottomColor="$borderColor">
        <FilterButton value="all" label={t('all') || 'All'} count={statusCounts.all} />
        <FilterButton value="draft" label={t('draft') || 'Draft'} count={statusCounts.draft} />
        <FilterButton value="sent" label={t('sent') || 'Sent'} count={statusCounts.sent} />
        <FilterButton value="shipped" label={t('shipped') || 'Shipped'} count={statusCounts.shipped} />
        <FilterButton value="received" label={t('received') || 'Received'} count={statusCounts.received} />
      </XStack>

      {/* Orders List */}
      <FlatList
        data={filteredOrders}
        renderItem={renderOrderItem}
        keyExtractor={item => item.id}
        contentContainerStyle={{ padding: 16, paddingBottom: 120 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[merchantColors.primary]} />}
        ListEmptyComponent={
          <YStack alignItems="center" paddingVertical={60} paddingHorizontal="$xl">
            <YStack marginBottom="$md">
              <ClipboardList size={64} color="#999" />
            </YStack>
            <Text fontSize={18} fontWeight="600" color="$color" marginBottom="$sm">
              {filter === 'all'
                ? (t('no_purchase_orders') || 'No purchase orders yet')
                : (t('no_orders_in_status') || `No ${filter} orders`)}
            </Text>
            <Text fontSize={14} color="$colorSecondary" textAlign="center">
              {t('create_po_hint') || 'Tap + to create a new purchase order'}
            </Text>
          </YStack>
        }
      />
    </YStack>
  )
}
